import React, {useEffect, useRef, useState} from 'react';
import L from "leaflet";
import * as turf from "@turf/turf";

const Sidebar = ({
  map,
  visibleMarkers,
  setVisibleMarkers,
  selectedMarkerButton,
  setSelectedMarkerButton,
  searchInput,
  sidebarMarkers,
  activeMarkerGroup,
  setActiveMarkerGroup,
  ...props
}) => {
  const initialMarkerTypeButton = useRef(null);
  const [sidebarMarkersLoaded, setSidebarMarkersLoaded] = useState(false);
  // Track active marker name.
  const [activeMarkerName, setActiveMarkerName] = useState('');
  const updateVisibleMarkers = (target, updatedMarkers) => {
    setSelectedMarkerButton(target);

    if (selectedMarkerButton === target) {
      setVisibleMarkers(sidebarMarkers);
      setSelectedMarkerButton(false);
    } else {
      setVisibleMarkers(updatedMarkers);
    }
  };

  const sortedMarkers = (markers) => {
    Object.keys(markers).forEach((category) => {
      markers[category].sort((a, b) => a.properties.name.localeCompare(b.properties.name));
    });

    return markers;
  };

  useEffect(() => {
    if (Object.keys(sidebarMarkers).length > 0 && !sidebarMarkersLoaded && initialMarkerTypeButton) {
      // Ensure the following code is only run once.
      setSidebarMarkersLoaded(true);

      // Set the first marker group as active on initial load.
      const sidebarMarkerType = Object.keys(sidebarMarkers)[0];
      const marker = sidebarMarkers[sidebarMarkerType];
      setActiveMarkerGroup(sidebarMarkerType);
      setVisibleMarkers({ [sidebarMarkerType]: marker });
      setSelectedMarkerButton(initialMarkerTypeButton.current);
    }
  }, [sidebarMarkers]);

  useEffect(() => {
    if (searchInput !== '') {
      setActiveMarkerGroup('');
      // Reset active marker name when search input is not empty.
      setActiveMarkerName('');
      if (selectedMarkerButton) {
        setSelectedMarkerButton(false);
      }
    } else {
      setSelectedMarkerButton(false);
    }
  }, [searchInput]);

  return (
    <div {...props}>
      {Object.keys(sidebarMarkers).map((markerType, markerTypeIndex) => (
        <ul key={markerType}>
          <li className="mb-2">
            <button
              ref={markerTypeIndex === 0 ? initialMarkerTypeButton : null}
              dangerouslySetInnerHTML={{
                __html: `${markerType}<span class="o-map__sidebar-button-count">${sidebarMarkers[markerType].length}</span>`,
              }}
              className={[
                'o-map__sidebar-button',
                'group',
                'py-5',
                'text-h4',
                'text-black',
                // Active class for group button.
                activeMarkerGroup === markerType && 'o-map__sidebar-button--active',
                markerType === 'Toilets' && 'o-map__sidebar-button--disable-child',
              ].filter(Boolean).join(' ')}
              // Conditionally add 'active' class.
              onClick={({ target }) => {
                // Set zoom to show roughly whole map when sidebar is clicked,
                // useful for zooming out after a maker group has been selected.
                map.setZoom(15);
                const isSameGroup = activeMarkerGroup === markerType;
                setActiveMarkerGroup(isSameGroup ? '' : markerType);
                // Reset active marker when switching groups.
                setActiveMarkerName('');
                setSelectedMarkerButton(isSameGroup ? '' : markerType);
                setVisibleMarkers(isSameGroup ? sidebarMarkers : { [markerType]: sidebarMarkers[markerType] });
              }}
            />

            <ul
              className={`${
                activeMarkerGroup !== '' && activeMarkerGroup !== markerType && 'hidden'
              }`}
            >
              {sortedMarkers(sidebarMarkers)[markerType].map((marker, i) => {
                const { coordinates, properties } = marker;
                const { name } = properties;

                return (
                  <li key={i}>
                    <button
                      dangerouslySetInnerHTML={{ __html: name }}
                      className={[
                        'o-map__sidebar-button',
                        'group',
                        'py-2.5',
                        'text-h4',
                        'font-normal',
                        'text-black',
                        // Active class for individual marker.
                        activeMarkerName === name && 'o-map__sidebar-sub-button--active',
                      ].filter(Boolean).join(' ')}
                      // Conditionally add 'active' class.
                      onClick={({ target }) => {
                        if (activeMarkerName === name) {
                          setActiveMarkerGroup('');
                          setActiveMarkerName('');
                          // Track individual marker click.
                          setVisibleMarkers(sidebarMarkers);
                        } else {``
                          setActiveMarkerGroup(markerType);
                          // Keep track of group.
                          setActiveMarkerName(name);
                          // Track individual marker click.
                          setVisibleMarkers({ [markerType]: [marker] });
                        }

                        if (coordinates) {
                          // eslint-disable-next-line react/prop-types
                          const newMarkers = coordinates.map((markerPosition) => {
                            const pos = turf.getGeom(turf.point(markerPosition));
                            // Flip the position of the lat and lang so, they're
                            // formatted correctly.
                            const flippedPosition = turf.flip(pos).coordinates;

                            // Create new marker instance that will be added to
                            // group.
                            return L.marker(flippedPosition);
                          });

                          const featureGroup = L.featureGroup(newMarkers);

                          // Set the map to show only the group of markers
                          // selected.
                          // eslint-disable-next-line react/prop-types
                          map.fitBounds(featureGroup.getBounds(), { maxZoom: 16.1 });
                        }
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          </li>
        </ul>
      ))}
    </div>
  );
};

export default Sidebar;
